import React, {
  useMemo, useState, useContext, useCallback
} from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import ArrowRight from '@material-ui/icons/ArrowForward';
import RoundedButton from 'components/MuiComponents/RoundedButton';
import MuiInput from '@material-ui/core/Input';
import Radio from '@material-ui/core/Radio';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { withTranslation } from 'react-i18next';
import PlaceAutocomplete from 'components/PlaceAutocomplete/Loadable';
import * as placesActions from 'redux/modules/searchBar';
import * as customizeActions from 'redux/modules/customizeBooking';
import * as bookingsActions from 'redux/modules/booking';
import * as plannerActions from 'redux/modules/planner';
import NoSsr from '@material-ui/core/NoSsr';
import isEmpty from 'lodash.isempty';
import {
  onErrorHandler,
  ErrorBoundaryFallbackComponent,
  withErrorBoundary
} from 'components/ErrorBoundry/ErrorBoundry';
import CountryContext from 'context/CountryContext';
import styled, { ThemeProvider } from 'styled-components';
import theme, { media } from 'theme/styled-theme';
import { sendGtagEvent } from 'utils/gtag';
import AirportDropdown from './AirportDropdown';

const Input = styled(MuiInput)`
  &&{
    margin: 0 8px;
    padding: 8px 14px;
    border: 1px solid rgb(230, 230, 230);
    box-shadow: rgba(0, 0, 0, 0.05) 0px 2px 13px 0px;
    border-radius: 4px;
    width: 123px;

    ${media.forPhoneOnly`
      width: 100%;
      margin: 0;
      height: 49px;
      line-height: 22px;
    `}
  }
`;

const MobileAvailOptions = styled.div`
  display: none;
  ${media.forPhoneOnly`
    display: flex;
    padding: 0.75rem;
    flex-direction: column;
  `}
`;

const SeparateLine = styled.div`
  display: block;
  background: ${props => props.theme.greyLight60};
  width: 100%;
  height: 1px;
`;

const Form = styled.div`
  display: flex;
  padding: 0.75rem;

  ${media.forPhoneOnly`
    flex-direction: column;
  `}
`;

const FieldContainer = styled.div`
  font-size: 0.75rem;
`;

const HideOnMobile = styled.div`
  ${media.forPhoneOnly`
    display: none;
  `}
`;

const DisplayOnMobile = styled.div`
  display: none;
  ${media.forPhoneOnly`
    display: block;
  `}
`;

const Label = styled.p`
  font-size: 12px;
  line-height: 15px;
  margin: 4px 0;

  ${media.forPhoneOnly`
    margin-top: 8px;
  `}
`;

const ForwardIcon = styled.span`
  width: 42px;
  transform: translateY(67%);
  display: flex;
  justify-content: center;
`;

const CircularProgress = styled(MuiCircularProgress)`
  && {
    color: white;
  }
`;

const AirportTransport = props => {
  const { code } = useContext(CountryContext);
  const [availing, setAvailing] = useState('pickup');
  const [buttonLoading, setButtonLoading] = useState(false);
  const {
    history,
    loadingAirportDestination,
    suggestionAirportDestination,
    searchAutocompleteAirportDestination,
    selectAirportDestinationPlaceId,
    airportDestination,
    clearAirportDestination,
    searchKeyWordPlaceAirportDestination,
    searchedAirport,
    placeSearchResultAirportDestination,
    clearAirportSearchResult,
    addPickupPlaceToState,
    addDropoffPlaceToState,
    airportAirport,
    clearEnquiry,
    resetPlannerPlan,
    t,
    selectAirportAirport,
    changeFlightNumber
  } = props;

  const buttonStyle = useMemo(() => ({
    root: {
      margin: '0',
      height: '52px',
      minWidth: '138px',
      padding: '0.875rem 1.25rem',
    }
  }), []);

  const handleCheckPrice = e => {
    e.stopPropagation();
    e.preventDefault();
    sendGtagEvent('Check Price from landing', 'Goal', 'Airport Transport');
    setButtonLoading(true);
    clearEnquiry();
    resetPlannerPlan();

    if (availing === 'pickup') {
      addPickupPlaceToState({ place: airportAirport, day: 0 });
      addDropoffPlaceToState({ place: airportDestination, day: 0 });
    } else {
      addPickupPlaceToState({ place: airportDestination, day: 0 });
      addDropoffPlaceToState({ place: airportAirport, day: 0 });
    }

    history.push({
      pathname: '/itinerary/customize/pricing',
      search: `?searchBar=true&country=${code}`,
      state: {
        oneway: true
      }
    });
  };

  const onFlightNumberChange = useCallback(
    e => { e.stopPropagation(); changeFlightNumber(e.target.value); },
    [changeFlightNumber],
  );

  const AutoCompleteEl = useMemo(() => (
    <PlaceAutocomplete
      name="airport"
      loadingSuggestion={loadingAirportDestination}
      suggestion={suggestionAirportDestination}
      searchAutocompletePlace={searchAutocompleteAirportDestination}
      selectPlaceId={selectAirportDestinationPlaceId}
      currentSelection={airportDestination}
      clearSelection={clearAirportDestination}
      searchKeyWordPlace={searchKeyWordPlaceAirportDestination}
      searched={searchedAirport}
      searchResult={placeSearchResultAirportDestination}
      clearSearchResult={clearAirportSearchResult}
    />
  ), [
    loadingAirportDestination,
    suggestionAirportDestination,
    searchAutocompleteAirportDestination,
    selectAirportDestinationPlaceId,
    airportDestination,
    clearAirportDestination,
    searchKeyWordPlaceAirportDestination,
    searchedAirport,
    placeSearchResultAirportDestination,
    clearAirportSearchResult,
  ]);

  const onOptionChange = useCallback(e => {
    e.stopPropagation();
    setAvailing(e.target.value);
  }, []);

  const AvailingOptionEl = useMemo(() => (
    <div>
      <Label>{t('SEARCHBAR.TAB.AIRPORT.OPTION')}</Label>
      <NoSsr>
        <RadioGroup
          row
          aria-label="availing option"
          name="availing option"
          value={availing}
          onChange={onOptionChange}
        >
          <FormControlLabel
            control={(
              <Radio
                data-cy="cy-radio-pickup"
                checked={availing === 'pickup'}
                color="primary"
                value="pickup"
              />
            )}
            label={t('SEARCHBAR.TAB.AIRPORT.OPTION_PICKUP')}
          />
          <FormControlLabel
            control={(
              <Radio
                data-cy="cy-radio-dropoff"
                checked={availing === 'dropoff'}
                color="primary"
                value="dropoff"
              />
            )}
            label={t('SEARCHBAR.TAB.AIRPORT.OPTION_DROPOFF')}
          />
        </RadioGroup>
      </NoSsr>
    </div>
  ), [availing]);

  const AirportDropdownEl = useMemo(() => (
    <AirportDropdown
      selectAirportAirport={selectAirportAirport}
      selectedAirport={airportAirport}
    />
  ), [selectAirportAirport, airportAirport]);

  return (
    <ThemeProvider theme={theme}>
      <div>
        <MobileAvailOptions data-cy="mobile">
          { AvailingOptionEl }
          <SeparateLine />
        </MobileAvailOptions>
        <Form>
          <FieldContainer>
            <Label>{t('SEARCHBAR.TAB.AIRPORT.FROM')}</Label>
            { availing === 'pickup' ? AirportDropdownEl : AutoCompleteEl }
          </FieldContainer>

          <FieldContainer>
            <HideOnMobile>
              <Label>&nbsp;</Label>
              <ForwardIcon><ArrowRight /></ForwardIcon>
            </HideOnMobile>
          </FieldContainer>

          <FieldContainer>
            <Label>{t('SEARCHBAR.TAB.AIRPORT.TO')}</Label>
            { availing === 'pickup' ? AutoCompleteEl : AirportDropdownEl }
          </FieldContainer>

          <FieldContainer>
            <HideOnMobile>
              <Label>&nbsp;</Label>
            </HideOnMobile>
            <DisplayOnMobile>
              <Label>Flight No. *</Label>
            </DisplayOnMobile>
            <Input
              disableUnderline
              placeholder={t('SEARCHBAR.TAB.AIRPORT.FLIGHT')}
              onChange={onFlightNumberChange}
            />
          </FieldContainer>
          <FieldContainer>
            <Label>&nbsp;</Label>
            <RoundedButton
              data-cy="cy-airport-checkprice"
              variant="contained"
              color="primary"
              key={t('CHECK_PRICE')}
              disabled={isEmpty(airportAirport) || isEmpty(airportDestination)}
              onClick={handleCheckPrice}
              fullWidth
              overwritestyles={buttonStyle}
            >
              { buttonLoading ? <CircularProgress size={24} /> : t('CHECK_PRICE') }
            </RoundedButton>
          </FieldContainer>
        </Form>
        <HideOnMobile data-cy="desktop">
          <SeparateLine />
          <Form>
            { AvailingOptionEl }
          </Form>
        </HideOnMobile>
      </div>
    </ThemeProvider>
  );
};

AirportTransport.propTypes = {
  history: PropTypes.objectOf(PropTypes.any).isRequired,

  // Planner Reducer
  addPickupPlaceToState: PropTypes.func.isRequired,
  addDropoffPlaceToState: PropTypes.func.isRequired,

  loadingAirportDestination: PropTypes.bool,
  suggestionAirportDestination: PropTypes.arrayOf(PropTypes.any),
  searchAutocompleteAirportDestination: PropTypes.func.isRequired,
  selectAirportDestinationPlaceId: PropTypes.func.isRequired,
  changeFlightNumber: PropTypes.func.isRequired,
  clearAirportDestination: PropTypes.func.isRequired,
  airportDestination: PropTypes.objectOf(PropTypes.any),
  selectAirportAirport: PropTypes.func.isRequired,
  airportAirport: PropTypes.objectOf(PropTypes.any),
  clearEnquiry: PropTypes.func.isRequired,
  resetPlannerPlan: PropTypes.func.isRequired,
  searchKeyWordPlaceAirportDestination: PropTypes.func.isRequired,
  searchedAirport: PropTypes.bool.isRequired,
  placeSearchResultAirportDestination: PropTypes.arrayOf(PropTypes.any).isRequired,
  clearAirportSearchResult: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

AirportTransport.defaultProps = {
  loadingAirportDestination: false,
  suggestionAirportDestination: [],
  airportDestination: {},
  airportAirport: {},
};

export default compose(
  withTranslation(['common']),
  withRouter,
  connect(
    state => ({
      loadingAirportDestination: state.searchBar?.loadingAirportDestination,
      suggestionAirportDestination: state.searchBar?.suggestionAirportDestination,
      airportDestination: state.searchBar?.airportDestination,
      airportAirport: state.searchBar?.airportAirport,
      searchedAirport: state.searchBar?.searchedAirport,
      placeSearchResultAirportDestination: state.searchBar?.placeSearchResultAirportDestination,
    }),
    {
      ...placesActions,
      ...customizeActions,
      ...bookingsActions,
      ...plannerActions,
    }
  )
)(withErrorBoundary(
  AirportTransport,
  ErrorBoundaryFallbackComponent,
  onErrorHandler,
));
